import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import werbekreisLogo from '@/assets/werbekreis-logo.png';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Mail, 
  Settings, 
  LogOut,
  Menu,
  Calendar,
  Gift,
  Lightbulb
} from "@/components/icons";
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const sidebarItems = [
  { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/admin/members', icon: Users, label: 'Mitglieder' },
  { href: '/admin/events', icon: Calendar, label: 'Events' },
  { href: '/admin/ideas', icon: Lightbulb, label: 'Ideen' },
  { href: '/admin/christmas-wishes', icon: Gift, label: 'Weihnachtswünsche' },
  { href: '/admin/newsletter', icon: Mail, label: 'Newsletter' },
  { href: '/admin/subscribers', icon: Users, label: 'Newsletter Abonnenten' },
];

function Sidebar({ className }: { className?: string }) {
  const location = useLocation();
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className={cn("flex flex-col h-full bg-card border-r", className)}>
      <div className="p-6 border-b">
        <Link to="/" className="flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity">
          <img 
            src={werbekreisLogo} 
            alt="Werbekreis Roding Logo" 
            className="h-10 w-auto"
          />
        </Link>
        {profile && (
          <p className="text-sm text-muted-foreground mt-1">
            {profile.first_name} {profile.last_name}
          </p>
        )}
      </div>
      
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={cn(
                    "flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors",
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "hover:bg-muted"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      
      <div className="p-4 border-t">
        <Button 
          variant="ghost" 
          className="w-full justify-start"
          onClick={handleSignOut}
        >
          <LogOut className="h-5 w-5 mr-3" />
          Abmelden
        </Button>
      </div>
    </div>
  );
}

export function AdminLayout() {
  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-64 shrink-0">
        <Sidebar className="h-screen" />
      </div>
      
      {/* Mobile Header with Sidebar */}
      <div className="md:hidden">
        <div className="flex items-center justify-between p-4 border-b bg-card">
          <h1 className="text-lg font-semibold">Admin Panel</h1>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              <Sidebar />
            </SheetContent>
          </Sheet>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}